/**
 * Canonical Firestore collection names. Centralised so screens never hard-code
 * a string and the "not-yet-projected" set is explicit.
 */
export const COLLECTIONS = {
  // identity
  users: "users",
  userNameChanges: "userNameChanges",
  kycVerifications: "kycVerifications",
  accountDeletionRequests: "accountDeletionRequests",
  // Per-platform minimum supported app build. Redacted projection readable by every signed-in
  // user (no `reason` / `updatedBy`); the console fetches those via the admin GET.
  appVersionPolicies: "appVersionPolicies",
  // Runtime feature flags / maintenance kill-switches. Redacted projection readable by anyone,
  // before sign-in (no `reason` / `updatedBy`); the console fetches those via the admin GET.
  featureFlags: "featureFlags",
  // wallet
  wallets: "wallets",
  walletAvailability: "walletAvailability",
  activityEntries: "activityEntries",
  ledgerEntries: "ledgerEntries",
  walletTransactions: "walletTransactions",
  payments: "payments",
  cardRefunds: "cardRefunds",
  cardRefundProofSubmissions: "cardRefundProofSubmissions",
  completedCardRefunds: "completedCardRefunds",
  sessionKeys: "sessionKeys",
  ownerOperations: "ownerOperations",
  walletMigrations: "walletMigrations",
  // Metadata ONLY — see `OwnerKeyBackupDoc`. The password-encrypted recovery secret itself is
  // never projected to Firestore and has no admin read surface anywhere.
  ownerKeyBackups: "ownerKeyBackups",
  // cross-chain
  depositAddresses: "depositAddresses",
  stablecoinReceiveAddresses: "stablecoinReceiveAddresses",
  crossChainDeposits: "crossChainDeposits",
  crossChainSends: "crossChainSends",
  // cards
  cards: "cards",
  cardNotifications: "cardNotifications",
  collateralWithdrawals: "collateralWithdrawals",
  // ramps
  deposits: "deposits",
  withdrawals: "withdrawals",
  virtualAccounts: "virtualAccounts",
  paymentAccounts: "paymentAccounts",
  // ramp sagas — the on-chain halves, owned by the wallet domain. These are where a stalled
  // ramp is actually diagnosable: `deposits` / `withdrawals` carry only the coarse state.
  forwarderSweeps: "forwarderSweeps",
  offrampBurns: "offrampBurns",
  // earn
  vaults: "vaults",
  earnMarketState: "earnMarketState",
  earnPositions: "earnPositions",
  earnOperations: "earnOperations",
  vaultApprovals: "vaultApprovals",
  // settlement-internal
  holds: "holds",
  settlementObligations: "settlementObligations",
  settlements: "settlements",
  reserveTopUpLegs: "reserveTopUpLegs",
  smartAccountOperations: "smartAccountOperations",
} as const;

export type CollectionName = (typeof COLLECTIONS)[keyof typeof COLLECTIONS];

/**
 * Collections the backend has NOT projected yet. The console shows an explanatory empty state
 * for these instead of the ordinary "no rows" one.
 *
 * Empty today. The five settlement-internal collections lived here until wallet `888ea8a`
 * shipped their projections; leaving them listed told an operator looking at a genuinely empty
 * collection that the backend had not built it yet. Keep this set honest — a stale entry is
 * worse than no entry, because it reads as a known gap rather than a real answer.
 */
export const NOT_YET_PROJECTED: ReadonlySet<CollectionName> = new Set([]);

export function isNotYetProjected(name: CollectionName): boolean {
  return NOT_YET_PROJECTED.has(name);
}
